import { useBookingCommerceStore } from '@/stores/bookingCommerce'
import { useBookingSelectionsStore } from '@/stores/bookingSelections'
import { type SessionTypeKey } from '@/stores/sessionType'
import { defineStore } from 'pinia'

import dayjs from 'dayjs'

import {
  buildSceneToBucketMap,
  CATEGORY_BUCKET,
  defaultRecommendedSessionTypeKeys,
  getRecommendedSessionTypeKeys
} from '@/lib/sessionCatalog'
import Sunny16CommerceCadyService from '@/services/Sunny16CommerceCadyService'

import type { BookingSchoolSimpleDto } from '@VelocitySquared/sunny16-commerce-dataobjects/storefront/booking'

import { bookingOpenDate } from '@/content/landing-page'
import { sceneInspirationModalFallback } from '@/content/sessions-listing/sceneInspirationModal'

interface MainStoreState {
  authToken: string | null
  showSidebar: boolean
  schoolList: BookingSchoolSimpleDto[]
  schoolListLoading: boolean
  selectedSchool: BookingSchoolSimpleDto | null
  /** The active school year for catalog path construction. */
  activeSchoolYear: number
  hasSeenSceneInspiration: boolean
  isRouteNavigating: boolean
  isAppProcessing: boolean
  appLoadingMessage: string
  showFindSchoolModal: boolean
  bookingOpenDate: Date
  /**
   * Epoch ms when catalog + content data was last loaded successfully.
   * Drives the 12h data-expiration policy in `lib/dataExpiration.ts`.
   * Null until the first successful `loadCatalogWithContent` call.
   */
  dataLoadedAt: number | null
  /**
   * Re-entrancy lock for `lib/dataExpiration.ts#expireAndReset`. Both the
   * router guard and the `bookingSelectionsStore.$subscribe` handler check
   * this and early-return so the reset's own `resetBooking()` /
   * `router.replace()` calls don't recurse.
   */
  isExpiringData: boolean
}

import { storeId } from '@/stores/storeId'

/** Static scene-to-bucket map built from fallback data for recommendation logic. */
const sceneToBucket = buildSceneToBucketMap(
  (
    sceneInspirationModalFallback as unknown as {
      categories: { identifier: string; scenes: { sceneId: string }[] }[]
    }
  ).categories,
  CATEGORY_BUCKET
)

export const useMainStore = defineStore(storeId.MAIN, {
  state: (): MainStoreState => ({
    authToken: null,
    showSidebar: false,
    schoolList: [],
    schoolListLoading: false,
    selectedSchool: null,
    activeSchoolYear: 2027,
    hasSeenSceneInspiration: false,
    isRouteNavigating: false,
    isAppProcessing: false,
    appLoadingMessage: 'Loading...',
    showFindSchoolModal: false,
    bookingOpenDate: bookingOpenDate,
    dataLoadedAt: null,
    isExpiringData: false
  }),
  getters: {
    /** True when either route navigation or a general app process (e.g. cart action) is in flight. */
    isAppLoading(): boolean {
      return this.isRouteNavigating || this.isAppProcessing
    },
    /**
     * Derive the recommended session type keys from the user's favorited scenes.
     * Cross-store dependency: reads `favoritedSceneIds` from the booking store.
     * Falls back to `defaultRecommendedSessionTypeKeys` when no scenes are favorited.
     * Returns up to 2 session type keys per the SY27 paired recommendation spec.
     *
     * RS+ schools return [] — recommendation badges are suppressed on the
     * RS+ Session Listing. The underlying lib helper `getRecommendedSessionTypeKeys`
     * stays RS+-agnostic per ADR 2026.04.20-002 (no flow logic in `src/lib/`);
     * this getter is the dispatch layer.
     */
    recommendedSessionTypeKeys(): SessionTypeKey[] {
      const bookingCommerceStore = useBookingCommerceStore()
      if (bookingCommerceStore.isRoadshowPlus) return []
      const bookingSelectionsStore = useBookingSelectionsStore()
      const favoritedSceneIds = bookingSelectionsStore.favoritedSceneIds
      if (favoritedSceneIds.length > 0) {
        return getRecommendedSessionTypeKeys(favoritedSceneIds, sceneToBucket)
      }
      return defaultRecommendedSessionTypeKeys
    },
    /** Returns true when the current date is past the booking open date. */
    bookingIsOpen(): boolean {
      return dayjs(this.bookingOpenDate).isBefore(dayjs())
    },
    /** Derive the catalog path from the selected school and active year. */
    catalogPath(): string {
      if (!this.selectedSchool) return ''
      const prefix = import.meta.env.VITE_CATALOG_PATH_PREFIX
      const state = this.selectedSchool.state.toLowerCase()
      const schoolId = this.selectedSchool.schoolId
      return `${prefix}/${this.activeSchoolYear}/${state}/${schoolId}`
    },
    /** Returns a predicate that checks if a given session type key is one of the recommended ones. */
    isSessionTypeRecommended(): (sessionTypeKey: SessionTypeKey) => boolean {
      return (sessionTypeKey) => this.recommendedSessionTypeKeys.includes(sessionTypeKey)
    }
  },
  actions: {
    toggleSidebar() {
      this.showSidebar = !this.showSidebar
    },
    setAuthToken(token: string) {
      this.authToken = token
    },
    /**
     * Toggle the general app-processing loading state. Optionally update the
     * loading message shown in the overlay. When turning processing off, the
     * message is left as-is so callers don't need to restore it.
     */
    setAppProcessing(isProcessing: boolean, message?: string): void {
      if (message !== undefined) {
        this.appLoadingMessage = message
      }
      this.isAppProcessing = isProcessing
    },
    /**
     * Load the full school list from the service.
     * Called once when the landing page mounts. No-ops if already loaded.
     */
    async loadSchoolList(): Promise<void> {
      if (this.schoolList.length > 0) return
      this.schoolListLoading = true
      try {
        const service = new Sunny16CommerceCadyService()
        this.schoolList = await service.getSchoolList()
      } catch (error) {
        console.error('[mainStore] loadSchoolList failed', error)
      } finally {
        this.schoolListLoading = false
      }
    },
    setSelectedSchool(school: BookingSchoolSimpleDto) {
      this.selectedSchool = school
    },
    clearSelectedSchool() {
      this.selectedSchool = null
    },
    /**
     * Stamp (or clear) the catalog/content load timestamp.
     * Called by `bookingCommerceStore.loadCatalogWithContent` on success
     * and by `lib/dataExpiration.ts` during the expire-and-reset flow.
     */
    setDataLoadedAt(ms: number | null) {
      this.dataLoadedAt = ms
    },
    setIsExpiringData(value: boolean) {
      this.isExpiringData = value
    },
    /** Reset all main store state to defaults. Called when starting a fresh session. */
    resetStore(): void {
      this.authToken = null
      this.showSidebar = false
      this.schoolList = []
      this.schoolListLoading = false
      this.selectedSchool = null
      this.activeSchoolYear = 2027
      this.hasSeenSceneInspiration = false
      this.isRouteNavigating = false
      this.isAppProcessing = false
      this.appLoadingMessage = 'Loading...'
      this.showFindSchoolModal = false
      this.bookingOpenDate = bookingOpenDate
      this.dataLoadedAt = null
      this.isExpiringData = false
    }
  },
  persist: {
    omit: [
      'bookingOpenDate',
      'isRouteNavigating',
      'isAppProcessing',
      'appLoadingMessage',
      'isExpiringData'
    ]
  }
})
